import { observable } from 'mobx';
import moment from 'moment';
import { toDomesticTel } from '@this/src/util';
import type { Moment } from '../lib/moment';
import type OrderItem from './order_item2';
import type {
  HotelElementGuestArgs,
  HotelElementGuestResponseArgs
} from './hotel_element_guest/hotel_element_guest2';
import HotelElementGuest, {
  convertHotelElementGuestResponseToArgs
} from './hotel_element_guest/hotel_element_guest2';
import type ElementDetailOptions from './element_detail_options';

export type HotelElementResponseArgs = {
  id: number;
  name: string;
  room_num: number;
  checkin_date: string;
  checkout_date: string;
  provider: string;
  plan_name: string;
  room_type: string;
  checkin_time?: string;
  checkout_time?: string;
  cancel_policy?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  tel?: string;
  refundable?: boolean;
  breakfast?: boolean;
  restricted: boolean;
  too_late: boolean;
  hotel_element_guests?: HotelElementGuestResponseArgs[];
  package_type?: string;
  marketlog_available?: boolean;
  hotel_reservation_number?: string;
  detail_path?: string;
  api_canceled_at?: string;
};

export type HotelElementArgs = {
  id: number;
  name: string;
  roomNum: number;
  checkinDate: Moment;
  checkoutDate: Moment;
  provider: string;
  planName: string;
  roomType: string;
  checkinTime?: string;
  checkoutTime?: string;
  cancelPolicy?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  tel?: string;
  refundable?: boolean;
  breakfast?: boolean;
  restricted: boolean;
  tooLate: boolean;
  hotelElementGuests: HotelElementGuestArgs[];
  packageType?: string;
  marketlogAvailable?: boolean;
  hotelReservationNumber?: string;
  detailPath?: string;
  apiCanceledAt: Moment | null;
};

export const convertHotelElementResponseToArgs = (response: HotelElementResponseArgs) => {
  const args: HotelElementArgs = {
    id: response.id,
    name: response.name,
    roomNum: response.room_num,
    checkinDate: moment(response.checkin_date),
    checkoutDate: moment(response.checkout_date),
    provider: response.provider,
    planName: response.plan_name,
    roomType: response.room_type,
    checkinTime: response.checkin_time,
    checkoutTime: response.checkout_time,
    cancelPolicy: response.cancel_policy,
    address: response.address,
    latitude: response.latitude,
    longitude: response.longitude,
    tel: response.tel,
    refundable: response.refundable,
    breakfast: response.breakfast,
    restricted: response.restricted,
    tooLate: response.too_late,
    hotelElementGuests: response.hotel_element_guests?.map(convertHotelElementGuestResponseToArgs) || [],
    packageType: response.package_type,
    marketlogAvailable: response.marketlog_available,
    hotelReservationNumber: response.hotel_reservation_number,
    detailPath: response.detail_path,
    apiCanceledAt: response.api_canceled_at ? moment(response.api_canceled_at) : null
  };
  return args;
};

export default class HotelElement {
  @observable id: number;
  @observable name: string;
  @observable roomNum: number;
  @observable checkinDate: Moment;
  @observable checkoutDate: Moment;
  @observable provider: string;
  @observable planName: string;
  @observable roomType: string;
  @observable checkinTime?: string;
  @observable checkoutTime?: string;
  @observable cancelPolicy?: string;
  @observable address?: string;
  @observable latitude?: number;
  @observable longitude?: number;
  @observable tel?: string;
  @observable refundable?: boolean;
  @observable breakfast?: boolean;
  @observable restricted: boolean;
  @observable tooLate: boolean;
  @observable hotelElementGuests: HotelElementGuest[] = [];
  @observable packageType?: string;
  @observable marketlogAvailable?: boolean;
  @observable hotelReservationNumber?: string;
  @observable detailPath?: string;
  @observable apiCanceledAt: Moment | null;

  constructor(args: HotelElementArgs) {
    this.id = args.id;
    this.name = args.name;
    this.roomNum = args.roomNum;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.provider = args.provider;
    this.planName = args.planName;
    this.roomType = args.roomType;
    this.checkinTime = args.checkinTime;
    this.checkoutTime = args.checkoutTime;
    this.cancelPolicy = args.cancelPolicy;
    this.address = args.address;
    this.latitude = args.latitude;
    this.longitude = args.longitude;
    this.tel = args.tel;
    this.refundable = args.refundable;
    this.breakfast = args.breakfast;
    this.restricted = args.restricted;
    this.tooLate = args.tooLate;
    this.hotelElementGuests = args.hotelElementGuests.map(guest => new HotelElementGuest(guest));
    this.packageType = args.packageType;
    this.marketlogAvailable = args.marketlogAvailable;
    this.hotelReservationNumber = args.hotelReservationNumber;
    this.detailPath = args.detailPath;
    this.apiCanceledAt = args.apiCanceledAt;
  }

  get type() {
    return 'hotel';
  }

  get startDate() {
    return this.checkinDate;
  }

  get endDate() {
    return this.checkoutDate;
  }

  get summary() {
    return `${this.checkinDate.format('YYYY-MM-DD')} 〜 ${this.checkoutDate.format('YYYY-MM-DD')}`;
  }

  get title() {
    return '宿泊';
  }

  get breakfastDescription() {
    let d = '\n朝食：';
    switch (this.breakfast) {
      case true:
        d += '有り';
        break;
      case false:
        d += '無し';
        break;
      default:
        d += 'お問合せください';
        break;
    }
    return d;
  }

  get marketLogType() {
    return this.type;
  }

  get isPackage() {
    return !!this.packageType || this.provider === 'wbf';
  }

  get isCanceled() {
    return this.provider === 'mynavi' && this.apiCanceledAt !== null;
  }

  get telDescription() {
    return this.tel ? `\n電話番号：${toDomesticTel(this.tel)}` : '';
  }

  get hotelMapAddressLink() {
    const GOOGLE_MAPS_BASE_URL = 'https://www.google.com/maps';
    if (this.address) {
      return `${GOOGLE_MAPS_BASE_URL}/search/${encodeURIComponent(this.address)}`;
    }
    if (this.latitude !== undefined && this.longitude !== undefined) {
      return `${GOOGLE_MAPS_BASE_URL}/search/?api=1&query=${this.latitude},${this.longitude}`;
    }
    return '';
  }

  get mappingDescription() {
    return `ホテル：${this.name}`;
  }

  get hotelTypeDetail() {
    return this.structuredHotelTypeDetail()
      .map(line => `${line.label || ''}${line.value || ''}\n`)
      .join('');
  }

  get roomTypeDetail() {
    return this.structuredRoomTypeDetail()
      .map(line => `${line.label || ''}${line.value || ''}\n`)
      .join('');
  }

  detail(orderItem: OrderItem, opts: ElementDetailOptions = {}) {
    return this.structuredDetail(orderItem, opts)
      .map(line => `${line.label}${line.value}\n`)
      .join('');
  }

  structuredDetail(_orderItem: OrderItem, opts: ElementDetailOptions = {}) {
    const lines = this.structuredHotelTypeDetail(opts);
    lines.push(...this.structuredRoomTypeDetail());

    return lines;
  }

  structuredHotelTypeDetail(opts: ElementDetailOptions = {}) {
    const lines = [];
    lines.push({
      label: opts.inlineRestricted && this.restricted ? 'RESTRICTED!宿泊プラン：' : '宿泊プラン：',
      value: this.planName ? this.planName : '-',
      translate: true,
      restricted: this.restricted
    });

    if (this.provider !== 'agoda') {
      lines.push({
        label: '部屋タイプ：',
        value: this.roomType ? this.roomType : '',
        translate: true
      });
    }
    lines.push({
      label: '',
      value: this.breakfastDescription,
      translate: true
    });
    return lines;
  }

  structuredRoomTypeDetail() {
    const lines: Array<{ label: string; value: string; translate: boolean }> = [];
    this.hotelElementGuests.forEach((guest, i) => {
      lines.push(
        {
          label: '',
          value: `<${i + 1}室目>`,
          translate: false
        },
        {
          label: '',
          value: guest.bedTypeDescriptionOverview,
          translate: false
        },
        {
          label: '',
          value: `${guest.guestLastName} ${guest.guestFirstName} (${guest.guestTel})`,
          translate: false
        }
      );
    });
    return lines;
  }
}
